// <faq-page> — progressive enhancement for the FAQ section (sections/faq.liquid).
// The page is fully functional with no JS (categories are always-open section headers,
// questions are native <details>, nav links are anchors). This element layers on:
// live search, sticky-nav active state, smooth scroll, deep linking (#category), and the
// mobile category <select>. Vanilla, self-registering, dependency-free.

class FaqPage extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.searchInput = this.ref('searchInput');
    this.searchClear = this.ref('searchClear');
    this.searchStatus = this.querySelector('.faq__search-status');
    this.navSelect = this.ref('navSelect');
    this.noResults = this.ref('noResults');

    this.categories = Array.from(this.querySelectorAll('[data-faq-category]'));
    this.navLinks = Array.from(this.querySelectorAll('[data-faq-nav]'));
    this.items = Array.from(this.querySelectorAll('[data-faq-item]'));

    // Pre-compute a lowercased search haystack per item once.
    for (const item of this.items) {
      const question = item.querySelector('.faq__q-question')?.textContent ?? '';
      const answer = item.querySelector('.faq__q-answer')?.textContent ?? '';
      const keywords = item.dataset.keywords ?? '';
      const category = item.dataset.categoryTitle ?? '';
      item._haystack = `${question} ${answer} ${keywords} ${category}`.toLowerCase();
    }

    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => this.onSearch(), { signal });
      this.searchClear?.addEventListener('click', () => this.clearSearch(), { signal });
    }

    for (const link of this.navLinks) {
      link.addEventListener('click', (e) => this.onNavClick(e, link.dataset.faqNav), { signal });
    }
    this.navSelect?.addEventListener('change', () => this.onNavClick(null, this.navSelect.value), { signal });

    window.addEventListener('hashchange', () => this.applyHash(), { signal });
    this.applyHash();

    this.setupActiveObserver();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.observer?.disconnect();
  }

  ref(name) {
    return this.querySelector(`[ref="${name}"]`);
  }

  // ---------------------------------------------------------------- Search
  onSearch() {
    const query = this.searchInput.value.trim().toLowerCase();
    this.searchClear.hidden = query.length === 0;
    if (query.length === 0) return this.clearSearch();

    let visibleCount = 0;
    for (const category of this.categories) {
      let categoryHasMatch = false;
      for (const item of category.querySelectorAll('[data-faq-item]')) {
        const match = item._haystack.includes(query);
        item.hidden = !match;
        if (match) {
          categoryHasMatch = true;
          visibleCount += 1;
          // Allow several answers open while searching: drop the exclusive `name`.
          if (item.hasAttribute('name')) {
            item.dataset.name = item.getAttribute('name');
            item.removeAttribute('name');
          }
          item.open = true;
        } else {
          item.open = false;
        }
      }
      category.hidden = !categoryHasMatch;
    }

    if (this.noResults) this.noResults.hidden = visibleCount > 0;
    this.announce(`${visibleCount} ${visibleCount === 1 ? 'result' : 'results'}`);
  }

  clearSearch() {
    if (this.searchInput) this.searchInput.value = '';
    if (this.searchClear) this.searchClear.hidden = true;
    if (this.noResults) this.noResults.hidden = true;

    for (const category of this.categories) {
      category.hidden = false;
      for (const item of category.querySelectorAll('[data-faq-item]')) {
        item.hidden = false;
        item.open = false;
        if (item.dataset.name) {
          item.setAttribute('name', item.dataset.name); // restore exclusive accordion
          delete item.dataset.name;
        }
      }
    }
    this.announce('');
  }

  announce(message) {
    if (this.searchStatus) this.searchStatus.textContent = message;
  }

  // ---------------------------------------------------------------- Navigation
  onNavClick(event, handle) {
    const category = this.categories.find((c) => c.dataset.faqCategory === handle);
    if (!category) return;
    event?.preventDefault();
    this.scrollToCategory(category);
    if (history.replaceState) history.replaceState(null, '', `#${handle}`);
    else location.hash = handle;
    this.setActive(handle);
  }

  scrollToCategory(category) {
    // Suppress the scroll-spy briefly so an explicit jump wins, even when the target
    // sits near the page bottom and can't scroll up into the active band.
    this.suppressSpyUntil = Date.now() + 900;
    category.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'start' });
  }

  applyHash() {
    const handle = decodeURIComponent(location.hash.replace(/^#/, ''));
    if (!handle) return;
    const category = this.categories.find((c) => c.dataset.faqCategory === handle);
    if (!category) return;
    this.scrollToCategory(category);
    this.setActive(handle);
  }

  setActive(handle) {
    for (const link of this.navLinks) {
      const active = link.dataset.faqNav === handle;
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    }
    if (this.navSelect && this.navSelect.value !== handle) this.navSelect.value = handle;
  }

  // ---------------------------------------------------------------- Active-on-scroll
  setupActiveObserver() {
    if (!('IntersectionObserver' in window) || this.categories.length === 0) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        if (this.suppressSpyUntil && Date.now() < this.suppressSpyUntil) return;
        for (const entry of entries) {
          if (entry.isIntersecting) this.setActive(entry.target.dataset.faqCategory);
        }
      },
      { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
    );
    for (const category of this.categories) this.observer.observe(category);
  }
}

if (!customElements.get('faq-page')) {
  customElements.define('faq-page', FaqPage);
}
